import React, { useState, useContext } from "react";
import { Container, Row, Col, Form, Spinner } from "react-bootstrap";
import { Send, MessageSquareWarning } from "lucide-react"; 
import { AuthContext } from "../../context/AuthContext"; 
import PopupMessage from "../../components/common/PopupMessage";
import "../../assets/styles/Login.css"; 

import { reportProblem } from "../../services/userService.js"; 

const ReportProblem = () => {
  const { user } = useContext(AuthContext); 

  const [formData, setFormData] = useState({
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation ثنائي اللغة عشان يناسب الستايل
    const newErrors = {};
    if (!formData.subject.trim()) newErrors.subject = "يرجى كتابة الموضوع | Please enter a subject";
    if (!formData.message.trim()) newErrors.message = "يرجى كتابة الرسالة | Please enter your message";
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);

    try {
      const payload = {
        userId: user?._id,
        userEmail: user?.email, 
        userName: user?.fullName,
        subject: formData.subject,
        message: formData.message,
      };
      const response = await reportProblem(payload);

      if (response) {
        setPopup({
          show: true,
          type: "success",
          message: response?.data?.message || "تم إرسال الرسالة بنجاح! | Message sent successfully!",
        });
        setFormData({ subject: "", message: "" });
      } else {
        throw new Error(response?.message || "Failed to send message");
      }

    } catch (error) {
      setPopup({
        show: true,
        type: "error",
        message: error?.response?.data?.message || "حدث خطأ ما. | Something went wrong.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Background Orbs */}
      <div className="background-wrapper">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
      </div>

      <div className="login-advanced-container" style={{ paddingTop: "100px", paddingBottom: "50px" }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={6} md={8} sm={12}> 
              
              <div className="login-card p-4 p-md-5 shadow-lg border-0" style={{ background: "rgba(15, 23, 42, 0.6)", backdropFilter: "blur(16px)" }}>
                
                {/* Header Section - Bilingual */}
                <div className="text-center mb-4">
                  <div 
                    style={{
                      width: '65px', height: '65px', background: 'rgba(99, 102, 241, 0.1)', 
                      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 15px auto', border: '1px solid rgba(99, 102, 241, 0.2)',
                      boxShadow: '0 0 20px rgba(99, 102, 241, 0.2)'
                    }}
                  >
                    <MessageSquareWarning size={32} color="#818cf8" />
                  </div>
                  
                  <div className="d-flex flex-column gap-1 mb-3">
                    <h2 className="fw-bold text-white mb-0" dir="rtl">تواصل مع الدعم الفني</h2>
                    <h3 className="text-white-50 mb-0" dir="ltr" style={{ fontSize: "1.2rem", fontWeight: "500" }}>Contact Support</h3>
                  </div>

                  <div className="d-flex flex-column gap-1">
                    <p className="text-light mb-0" dir="rtl" style={{ fontSize: "0.95rem" }}>
                      واجهتك مشكلة أو لديك اقتراح؟ أرسل لنا رسالة مباشرة.
                    </p>
                    <p className="text-secondary small mb-0" dir="ltr">
                      Have an issue or suggestion? Send us a message directly.
                    </p>
                  </div>
                </div>

                {/* Form Section */}
                <Form onSubmit={handleSubmit} autoComplete="off">
                  
                  {/* Subject */}
                  <div className="input-group-advanced mb-4">
                    <label className="input-label d-flex justify-content-between w-100 px-1">
                      <span dir="ltr" className="text-muted">Subject</span>
                      <span dir="rtl" className="fw-bold" style={{ color: "#cbd5e1" }}>الموضوع</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      placeholder="بخصوص ماذا؟ | What is this about?"
                      className={`advanced-input ${errors.subject ? "is-invalid" : ""}`}
                      value={formData.subject}
                      onChange={handleChange}
                      style={{ textAlign: "right" }} // بنخلي الكتابة تبدأ من اليمين عشان العربي هو الأساس
                    />
                    {errors.subject && <small className="text-danger mt-1 d-block text-center">{errors.subject}</small>}
                  </div>

                  {/* Message (Textarea) */}
                  <div className="input-group-advanced mb-4">
                    <label className="input-label d-flex justify-content-between w-100 px-1">
                      <span dir="ltr" className="text-muted">Message</span>
                      <span dir="rtl" className="fw-bold" style={{ color: "#cbd5e1" }}>الرسالة</span>
                    </label>
                    <textarea
                      name="message"
                      placeholder="اكتب رسالتك هنا... | Type your message here..."
                      className={`advanced-input ${errors.message ? "is-invalid" : ""}`}
                      value={formData.message}
                      onChange={handleChange}
                      style={{ minHeight: '150px', resize: 'none', textAlign: "right" }} 
                    ></textarea>
                    {errors.message && <small className="text-danger mt-1 d-block text-center">{errors.message}</small>}
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    className="btn-primary-advanced mt-3 w-100 d-flex flex-column align-items-center justify-content-center gap-1 py-3"
                    disabled={loading}
                    style={{ transition: "all 0.3s ease" }}
                  >
                    {loading ? (
                      <div className="d-flex align-items-center gap-2">
                        <Spinner as="span" animation="border" size="sm" />
                        <span>جاري الإرسال... | Sending...</span>
                      </div>
                    ) : (
                      <>
                        <span className="fw-bold d-flex align-items-center gap-2" style={{ fontSize: "1.1rem" }}>
                          إرسال الرسالة <Send size={18} />
                        </span>
                        <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>Send Message</span>
                      </>
                    )}
                  </button>

                </Form>
              </div>

            </Col>
          </Row>
        </Container>

        {popup.show && (
            <PopupMessage
                type={popup.type}
                message={popup.message}
                onClose={() => setPopup({ ...popup, show: false })}
            />
        )}
      </div>
    </>
  );
};

export default ReportProblem;

// import React, { useState, useContext } from "react";
// import { Container, Row, Col, Form, Spinner } from "react-bootstrap";
// import { Send, MessageSquareWarning } from "lucide-react"; // أيقونة معبرة
// import { AuthContext } from "../../context/AuthContext"; 
// import PopupMessage from "../../components/common/PopupMessage";
// import "../../assets/styles/Login.css"; // هنستخدم نفس ستايل اللوجين عشان منكتبش CSS جديد

// // دالة الـ API بتاعتك اللي بتبعت الايميل
// import { reportProblem } from "../../services/userService.js"; 

// const ReportProblem = () => {
//   const { user } = useContext(AuthContext); 

//   const [formData, setFormData] = useState({
//     subject: "",
//     message: "",
//   });

//   const [loading, setLoading] = useState(false);
//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });
//   const [errors, setErrors] = useState({});

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({ ...prev, [name]: value }));
//     if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     // Validation بسيط
//     const newErrors = {};
//     if (!formData.subject.trim()) newErrors.subject = "Please enter a subject";
//     if (!formData.message.trim()) newErrors.message = "Please enter your message";
    
//     if (Object.keys(newErrors).length > 0) {
//       setErrors(newErrors);
//       return;
//     }

//     setLoading(true);

//     try {
//       // الداتا اللي رايحة للباك عشان تتبعت في الايميل
//       const payload = {
//         userId: user?._id,
//         userEmail: user?.email, // عشان الادمن يعرف يرد عليه
//         userName: user?.fullName,
//         subject: formData.subject,
//         message: formData.message,
//       };
//       const response = await reportProblem(payload);

//       if (response) {
//         setPopup({
//           show: true,
//           type: "success",
//           message: response?.data?.message || "Message sent successfully! We will contact you soon.",
//         });
//         setFormData({ subject: "", message: "" });
//       } else {
//         throw new Error(response?.message || "Failed to send message");
//       }

//     } catch (error) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: error?.response?.data?.message || "Something went wrong.",
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       {/* Background Orbs */}
//       <div className="background-wrapper">
//         <div className="orb orb-1"></div>
//         <div className="orb orb-2"></div>
//       </div>

//       <div className="login-advanced-container" style={{ paddingTop: "100px", paddingBottom: "50px" }}>
//         <Container>
//           <Row className="justify-content-center">
//             <Col lg={6} md={8} sm={12}> {/* عرض مناسب للفورم */}
              
//               <div className="login-card p-4 p-md-5">
                
//                 {/* Header بسيط وشيك */}
//                 <div className="text-center mb-4">
//                   <div 
//                     style={{
//                       width: '60px', height: '60px', background: 'rgba(99, 102, 241, 0.1)', 
//                       borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
//                       margin: '0 auto 15px auto', border: '1px solid rgba(99, 102, 241, 0.2)'
//                     }}
//                   >
//                     <MessageSquareWarning size={28} color="#6366f1" />
//                   </div>
//                   <h3 className="fw-bold text-white mb-2">Contact Support</h3>
//                   <p className="text-secondary small">
//                     Have an issue or suggestion? Send us a message directly.
//                   </p>
//                 </div>

//                 <Form onSubmit={handleSubmit} autoComplete="off">
                  
//                   {/* Subject */}
//                   <div className="input-group-advanced">
//                     <label className="input-label">Subject</label>
//                     <input
//                       type="text"
//                       name="subject"
//                       placeholder="What is this about?"
//                       className={`advanced-input ${errors.subject ? "is-invalid" : ""}`}
//                       value={formData.subject}
//                       onChange={handleChange}
//                     />
//                     {errors.subject && <small className="text-danger mt-1 d-block">{errors.subject}</small>}
//                   </div>

//                   {/* Message (Textarea) */}
//                   <div className="input-group-advanced">
//                     <label className="input-label">Message</label>
//                     <textarea
//                       name="message"
//                       placeholder="Type your message here..."
//                       className={`advanced-input ${errors.message ? "is-invalid" : ""}`}
//                       value={formData.message}
//                       onChange={handleChange}
//                       style={{ minHeight: '150px', resize: 'none' }} // منع تغيير الحجم للحفاظ على الشكل
//                     ></textarea>
//                     {errors.message && <small className="text-danger mt-1 d-block">{errors.message}</small>}
//                   </div>

//                   {/* Submit Button */}
//                   <button
//                     type="submit"
//                     className="btn-primary-advanced mt-2 d-flex align-items-center justify-content-center gap-2"
//                     disabled={loading}
//                   >
//                     {loading ? (
//                       <>
//                         <Spinner as="span" animation="border" size="sm" />
//                         Sending...
//                       </>
//                     ) : (
//                       <>
//                         Send Message <Send size={18} />
//                       </>
//                     )}
//                   </button>

//                 </Form>
//               </div>

//             </Col>
//           </Row>
//         </Container>

//         {popup.show && (
//             <PopupMessage
//                 type={popup.type}
//                 message={popup.message}
//                 onClose={() => setPopup({ ...popup, show: false })}
//             />
//         )}
//       </div>
//     </>
//   );
// };

// export default ReportProblem;